import React, {
    Dispatch,
    FC,
    memo,
    PropsWithChildren,
    SetStateAction,
    useCallback,
    useMemo,
} from 'react';

import type { ColDef, FirstDataRenderedEvent } from 'ag-grid-community';
import { RowNode } from 'ag-grid-community';

//components
import AGTable from '../../../../components/ag-table/AGTable';
import styled from 'styled-components';

import aampeTheme from '../../../../constants/aampeTheme';

//models
import { LabelWithVariantsModel } from '../../../../models/label.model';

interface LabelTableProps {
    data?: LabelWithVariantsModel[];
    isLoading: boolean;
    setCurrentLabel: Dispatch<
        SetStateAction<LabelWithVariantsModel | undefined>
    >;
}
interface StyledProps {
    isSelected: boolean;
}
const LabelTable: FC<PropsWithChildren<LabelTableProps>> = ({
    setCurrentLabel,
    data,
    isLoading,
}) => {
    const onCellClicked = useCallback(
        (event: any) => {
            if (event.column.getColId() === 'label_name') {
                setCurrentLabel(event.data);
            }
            event.api.deselectAll();
            event.node.setSelected(true);
            event.api.refreshCells({
                force: true,
            });
        },
        [setCurrentLabel],
    );

    const customCellRenderer = (params: any) => {
        return (
            <StyledSpan isSelected={params.node.isSelected()}>
                {params.value}
            </StyledSpan>
        );
    };

    const onFirstDataRendered = useCallback(
        (event: FirstDataRenderedEvent) => {
            const firstRowNode: RowNode | undefined =
                event.api.getDisplayedRowAtIndex(0) as RowNode;

            if (firstRowNode && firstRowNode instanceof RowNode) {
                firstRowNode.setSelected(true);
                firstRowNode.setSelectedInitialValue(true);
                firstRowNode.selectThisNode(true);
                setCurrentLabel(firstRowNode.data);
            }
        },
        [setCurrentLabel],
    );
    const columns = useMemo<ColDef<LabelWithVariantsModel>[]>(
        () => [
            {
                headerName: 'Label',
                field: 'label_name',
                width: 230,
                onCellClicked: onCellClicked,
                sort: undefined,
                sortingOrder: undefined,
                cellClass: 'selected-label-row',
                cellRenderer: customCellRenderer,
            },
        ],

        [onCellClicked],
    );

    return (
        <StyledAGTable
            rowData={data}
            columns={columns}
            isDataLoading={isLoading}
            rowStyle={{
                borderBottom: 'none',
            }}
            customOnFirstData={onFirstDataRendered}
        />
    );
};
const StyledSpan = styled.span.withConfig({
    shouldForwardProp: (prop) => prop !== 'isSelected',
})<StyledProps>(({ isSelected }) => ({
    backgroundColor: isSelected
        ? aampeTheme.colors.interactiveWG01
        : aampeTheme.colors.backgroundWG01,
    lineHeight: aampeTheme.size.lineHeight.tall,
    padding: aampeTheme.spacing4(isSelected ? 2 : 1.5, 2),
    color: isSelected
        ? aampeTheme.colors.neutral09
        : aampeTheme.colors.textAccessibleWG01,
    fontWeight: isSelected
        ? aampeTheme.weights.medium
        : aampeTheme.weights.light,
    '&:hover': {
        backgroundColor: aampeTheme.colors.backgroundWG01,
        color: aampeTheme.colors.neutral09,
        fontWeight: aampeTheme.weights.medium,
    },
}));

const StyledAGTable = styled(AGTable)({
    marginTop: 0,
    padding: 0,
    height: '424px',
    width: '200px',
});
export default memo(LabelTable);
